import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const blogData = {
  'construccion-2026': {
    title: 'La Construcción en 2026',
    subtitle: 'Nuevas técnicas y materiales revolucionan la industria',
    date: '15 Diciembre 2025',
    readTime: '8 min lectura',
    image: 'https://customer-assets.emergentagent.com/job_eficapro-pro/artifacts/bsvp1xxv_blog-2026.jpg',
    content: `
      <h2>Revolución en Materiales de Construcción</h2>
      <p>El año 2026 marca un punto de inflexión en la industria de la construcción. Las nuevas técnicas y materiales están transformando fundamentalmente cómo concebimos y ejecutamos los proyectos de infraestructura.</p>
      
      <h3>Construcción Modular</h3>
      <p>La construcción modular ha dejado de ser una alternativa para convertirse en la opción preferida para proyectos de vivienda multifamiliar. Esta metodología permite reducir los tiempos de construcción hasta en un 50%, minimizando además el impacto ambiental y los residuos generados en obra.</p>
      
      <h3>Concreto Autorreparable</h3>
      <p>El concreto autorreparable, que utiliza bacterias encapsuladas para sellar grietas automáticamente, está revolucionando la durabilidad de las estructuras. Esta tecnología extiende significativamente la vida útil de los edificios y reduce los costos de mantenimiento a largo plazo.</p>
      
      <h3>Impresión 3D en Construcción</h3>
      <p>La impresión 3D de componentes estructurales ha demostrado reducir el desperdicio de materiales en un 30%. Desde muros completos hasta elementos decorativos complejos, esta tecnología permite una precisión y personalización sin precedentes.</p>
      
      <h3>Maderas de Ingeniería (Mass Timber)</h3>
      <p>Las maderas de ingeniería, como el CLT (Cross-Laminated Timber), están ganando terreno en la construcción de edificios de mediana altura. Estos materiales combinan la sostenibilidad de la madera con resistencia estructural comparable al acero y el concreto.</p>
      
      <h3>Polímeros Reforzados</h3>
      <p>Los polímeros reforzados con fibra de carbono ofrecen una alternativa ligera y extremadamente resistente para aplicaciones estructurales, especialmente en zonas de alta actividad sísmica donde la reducción de peso es crítica.</p>
      
      <h2>El Futuro es Ahora</h2>
      <p>En EFICAPRO, estamos comprometidos con la adopción de estas innovaciones. Nuestro enfoque se centra en acelerar procesos, mejorar la huella de carbono de nuestros proyectos, y garantizar estructuras con máxima durabilidad y resistencia ante cualquier eventualidad.</p>
    `
  },
  'tecnologia-ia': {
    title: 'Tecnología e IA en Proyectos',
    subtitle: 'Cómo la inteligencia artificial transforma la ejecución de obras',
    date: '10 Diciembre 2025',
    readTime: '10 min lectura',
    image: 'https://customer-assets.emergentagent.com/job_eficapro-pro/artifacts/9hyg0b8p_blog-tecnologia.JPG',
    content: `
      <h2>La Era Digital en la Construcción</h2>
      <p>La tecnología y la inteligencia artificial se han convertido en aliados indispensables para la construcción moderna. En EFICAPRO, consideramos estas herramientas como nuestro brazo derecho para una ejecución impecable y transparente.</p>
      
      <h3>Diseño Generativo con IA</h3>
      <p>Los algoritmos de inteligencia artificial permiten explorar miles de alternativas de diseño en minutos, optimizando automáticamente factores como la distribución de cargas estructurales, la eficiencia energética y el aprovechamiento de la luz natural.</p>
      
      <h3>Drones con Sensores LiDAR</h3>
      <p>Los drones equipados con sensores LiDAR han revolucionado los levantamientos topográficos y la supervisión de avances de obra. Con precisión milimétrica, estos dispositivos permiten detectar desviaciones en tiempo real, anticipando problemas antes de que se conviertan en costosos retrabajos.</p>
      
      <h3>Software de Planificación Predictiva</h3>
      <p>Las plataformas de planificación basadas en machine learning pueden anticipar retrasos logísticos, identificar cuellos de botella y ajustar automáticamente los cronogramas de obra. Esta capacidad predictiva ha demostrado reducir los sobrecostos por imprevistos hasta en un 40%.</p>
      
      <h3>BIM Nivel 3</h3>
      <p>El Building Information Modeling en su nivel más avanzado permite la integración en tiempo real de todos los especialistas del proyecto en una nube de datos unificada. Esta colaboración sin precedentes elimina interferencias entre disciplinas y garantiza una coordinación perfecta.</p>
      
      <h2>Transparencia y Control Total</h2>
      <p>La digitalización completa del proceso constructivo nos permite ofrecer a nuestros clientes visibilidad total sobre el avance de sus proyectos. Dashboards en tiempo real, reportes automatizados y trazabilidad completa son ahora el estándar en todos nuestros desarrollos.</p>
    `
  },
  'construccion-mexico': {
    title: 'La Construcción en México Actual',
    subtitle: 'Oportunidades y desafíos en el panorama nacional',
    date: '5 Diciembre 2025',
    readTime: '7 min lectura',
    image: 'https://customer-assets.emergentagent.com/job_eficapro-pro/artifacts/tbk7xo9s_blog-mexico.jpg',
    content: `
      <h2>Un Sector en Transformación</h2>
      <p>México enfrenta un momento único en su historia constructiva. La convergencia de factores económicos, demográficos y tecnológicos está creando oportunidades sin precedentes para el desarrollo de infraestructura de calidad.</p>
      
      <h3>Resiliencia ante Retos Económicos</h3>
      <p>A pesar de los desafíos macroeconómicos globales, el sector de la construcción en México ha demostrado una notable resiliencia. El crecimiento poblacional sostenido y la demanda de vivienda de calidad mantienen activo al sector, especialmente en zonas metropolitanas.</p>
      
      <h3>Densificación Urbana Inteligente</h3>
      <p>La vivienda multifamiliar se ha posicionado como la solución clave para la densificación urbana responsable. En ciudades como Guadalajara, el desarrollo vertical permite aprovechar mejor la infraestructura existente mientras se reduce la expansión horizontal descontrolada.</p>
      
      <h3>El Efecto Nearshoring</h3>
      <p>El fenómeno del nearshoring está impulsando una demanda sin precedentes de inmuebles comerciales e industriales. Empresas de todo el mundo están reubicando sus operaciones en México, creando necesidades de infraestructura que el sector está preparado para satisfacer.</p>
      
      <h3>Sostenibilidad como Requisito</h3>
      <p>La sostenibilidad ha dejado de ser opcional. Los nuevos desarrollos integran desde su concepción sistemas de ahorro hídrico y energético, certificaciones ambientales y materiales de bajo impacto. Esta tendencia no solo responde a regulaciones, sino a la demanda del mercado.</p>
      
      <h2>EFICAPRO: Motor de Transformación</h2>
      <p>Como constructora líder en Jalisco, asumimos nuestro rol como agentes de transformación estructural nacional. Nuestra capacidad técnica, combinada con un profundo conocimiento del mercado mexicano, nos posiciona como el socio ideal para proyectos de cualquier escala.</p>
    `
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogData[slug];

  if (!post) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] pt-20">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-[#203864] mb-4">Artículo no encontrado</h1>
            <p className="text-[#7F7F7F] mb-8">El artículo que buscas no existe o ha sido movido.</p>
            <Link 
              to="/"
              className="inline-flex items-center gap-2 bg-[#2E75B6] text-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-[#203864] transition-colors"
            >
              <ArrowLeft size={18} />
              Volver al Inicio
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="pt-20" data-testid="blog-post-page">
        {/* Hero */}
        <div className="relative h-[50vh] md:h-[60vh]">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#203864] via-[#203864]/50 to-transparent"></div>
          
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
            <div className="max-w-4xl mx-auto">
              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft size={18} />
                Volver al Blog
              </motion.button>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4"
              >
                {post.title}
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-xl text-white/90 mb-6"
              >
                {post.subtitle}
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-6 text-white/70"
              >
                <span className="flex items-center gap-2">
                  <Calendar size={16} />
                  {post.date}
                </span>
                <span className="flex items-center gap-2">
                  <Clock size={16} />
                  {post.readTime}
                </span>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Content */}
        <article className="py-16 md:py-24 bg-white">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: post.content }}
              data-testid="blog-content"
            />

            {/* Share */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-12 pt-8 border-t border-gray-200"
            >
              <div className="flex items-center justify-between">
                <span className="text-[#7F7F7F]">¿Te resultó útil este artículo?</span>
                <button className="flex items-center gap-2 text-[#2E75B6] hover:text-[#203864] transition-colors">
                  <Share2 size={18} />
                  Compartir
                </button>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-12 bg-[#F8F9FA] p-8 md:p-12 text-center"
            >
              <h3 className="text-2xl font-bold text-[#203864] mb-4">
                ¿Listo para tu próximo proyecto?
              </h3>
              <p className="text-[#7F7F7F] mb-6">
                Contáctanos y descubre cómo podemos transformar tu visión en realidad.
              </p>
              <Link
                to="/#contacto"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                  setTimeout(() => {
                    document.querySelector('#contacto')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="inline-flex items-center gap-2 bg-[#2E75B6] text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-[#203864] transition-colors"
                data-testid="blog-cta"
              >
                Solicitar Cotización
              </Link>
            </motion.div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
};

export default BlogPost;
